// FilterBar — unified configurable filter card used by every report page.
//
// Two visual rows (each independently optional):
//   - Top row    ("OKRES")  — gray, for date/period controls + right-side timestamp
//   - Bottom row ("FILTRY") — white, for filter chips + reset button
//
// Both rows accept the same `type`-driven field schema, so you can put a date
// picker among filters or a select among periods if it fits semantically.
//
// Field types (set via `type` on each entry):
//
//   select     — pill that opens a dropdown
//                { type, key, label, value, display?, options, onChange, hideWhen? }
//
//   segmented  — inline pill-segmented control (instant pick, no dropdown)
//                { type, key, label, value, options, onChange }
//
//   date       — native HTML date input pill
//                { type, key, label, value: 'YYYY-MM-DD', onChange, min?, max? }
//
//   dateRange  — two date inputs joined by an em-dash
//                { type, key, labelFrom?, labelTo?, valueFrom, valueTo, onChangeFrom, onChangeTo }
//
//   toggle     — checkbox-style chip (active = filled)
//                { type, key, label, value: bool, onChange }
//
//   number     — numeric input pill (small)
//                { type, key, label, value, onChange, step?, min?, max? }
//
//   custom     — escape hatch; render arbitrary node inline
//                { type, key, render: () => ReactNode }
//
// Each entry may carry `hideWhen` (boolean) to conditionally skip without
// re-shaping the array.
//
// Reset:
//   onReset()    — called by the "× Wyczyść N" button. The component counts
//                  fields whose `value` is "active" (non-null, non-empty,
//                  not 0/false) to drive the badge.
//
// Custom sections:
//   <FilterBar> can also accept arbitrary child rows via `extraRows={[...]}`
//   for scenarios that don't fit the period/filter dichotomy.
//
import React, { useState, useRef, useEffect } from 'react'
import { theme as defaultTheme, T } from './tokens'
import { Icon } from './icons'

export default function FilterBar({
  periods = [], periodRight = null, periodLabel = 'Okres',
  filters = [], filtersLabel = 'Filtry',
  onReset,
  extraRows = [],
  theme = defaultTheme,
}) {
  const visiblePeriods = periods.filter(p => p && !p.hideWhen)
  const visibleFilters = filters.filter(f => f && !f.hideWhen)
  const activeCount = visibleFilters.filter(f => isActiveValue(f.value)).length
                    + visiblePeriods.filter(p => p.countAsActive && isActiveValue(p.value)).length

  const showPeriodRow = visiblePeriods.length > 0 || periodRight
  const showFilterRow = visibleFilters.length > 0
  const showExtras    = extraRows.length > 0

  if (!showPeriodRow && !showFilterRow && !showExtras) return null

  return (
    <div style={{
      background: theme.surface, border: `1px solid ${theme.border}`,
      borderRadius: 10, overflow: 'visible',
    }}>
      {showPeriodRow && (
        <div style={{
          padding: '12px 16px',
          display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
          borderBottom: (showFilterRow || showExtras) ? `1px solid ${theme.border}` : 'none',
          background: theme.bgSub,
        }}>
          {periodLabel && <Label theme={theme}>{periodLabel}</Label>}
          {visiblePeriods.map(p => <Field key={p.key} field={p} theme={theme} />)}
          <div style={{ flex: 1 }} />
          {periodRight && (
            <span style={{
              fontFamily: T.fontMono, fontSize: 11, color: theme.textDim,
            }}>{periodRight}</span>
          )}
        </div>
      )}

      {showFilterRow && (
        <div style={{
          padding: '12px 16px',
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          borderBottom: showExtras ? `1px solid ${theme.border}` : 'none',
        }}>
          {filtersLabel && <Label theme={theme} style={{ marginRight: 6 }}>{filtersLabel}</Label>}
          {visibleFilters.map(f => <Field key={f.key} field={f} theme={theme} />)}
          <div style={{ flex: 1 }} />
          {activeCount > 0 && (
            <button onClick={onReset} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '5px 10px', borderRadius: 7,
              background: 'transparent', border: `1px solid ${theme.border}`,
              color: theme.textSub, fontSize: 12, cursor: 'pointer',
              fontFamily: T.fontUI,
            }}>
              <span style={{ fontSize: 14, lineHeight: 0 }}>×</span>
              Wyczyść {activeCount}
            </button>
          )}
        </div>
      )}

      {extraRows.map((row, i) => (
        <div key={i} style={{
          padding: '12px 16px',
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          borderBottom: i === extraRows.length - 1 ? 'none' : `1px solid ${theme.border}`,
        }}>
          {row.label && <Label theme={theme} style={{ marginRight: 6 }}>{row.label}</Label>}
          {(row.fields || []).filter(f => f && !f.hideWhen).map(f => (
            <Field key={f.key} field={f} theme={theme} />
          ))}
          {row.right && <><div style={{ flex: 1 }} />{row.right}</>}
        </div>
      ))}
    </div>
  )
}

// ─ Field dispatcher ─────────────────────────────────────────────
function Field({ field, theme }) {
  switch (field.type || 'select') {
    case 'segmented': return <SegmentedField field={field} theme={theme} />
    case 'date':      return <DateField field={field} theme={theme} />
    case 'dateRange': return <DateRangeField field={field} theme={theme} />
    case 'toggle':    return <ToggleField field={field} theme={theme} />
    case 'number':    return <NumberField field={field} theme={theme} />
    case 'custom':    return <>{field.render()}</>
    case 'select':
    default:          return <SelectField field={field} theme={theme} />
  }
}

// ─ Common helpers ───────────────────────────────────────────────
const isActiveValue = (v) =>
  v != null && v !== '' && v !== false && !(Array.isArray(v) && v.length === 0)

function Label({ children, theme, style }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, color: theme.textDim,
      letterSpacing: 0.6, textTransform: 'uppercase',
      fontFamily: T.fontUI,
      ...style,
    }}>{children}</span>
  )
}

function PillFrame({ active, children, theme, padding = '6px 10px', style }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding,
      background: active ? theme.bgSub : theme.surface,
      border: `1px solid ${active ? theme.borderStrong : theme.border}`,
      borderRadius: 7,
      fontFamily: T.fontUI,
      ...style,
    }}>{children}</div>
  )
}

function useClickOutside(ref, handler) {
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) handler()
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [ref, handler])
}

function normalizeOptions(options) {
  return (options || []).map(o =>
    (typeof o === 'object' && o !== null)
      ? { value: o.value, label: o.label ?? String(o.value) }
      : { value: o, label: String(o) }
  )
}

// ─ Select (chip + dropdown) ─────────────────────────────────────
function SelectField({ field, theme }) {
  const { label, value, display, options, onChange, allowClear = true } = field
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useClickOutside(ref, () => setOpen(false))
  const active = isActiveValue(value)
  const opts = normalizeOptions(options)
  const shown = active ? (display ?? (opts.find(o => o.value === value)?.label ?? String(value))) : null
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(v => !v)} style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '6px 10px',
        background: active ? theme.bgSub : theme.surface,
        border: `1px solid ${active ? theme.borderStrong : theme.border}`,
        borderRadius: 7, cursor: 'pointer', fontFamily: T.fontUI,
        fontSize: 12.5,
        color: active ? theme.text : theme.textSub,
        maxWidth: 320,
      }}>
        <span style={{ color: active ? theme.textDim : theme.textSub, whiteSpace: 'nowrap' }}>
          {label}{active ? ':' : ''}
        </span>
        {active ? (
          <span style={{
            fontWeight: 600,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            maxWidth: 200,
          }}>{shown}</span>
        ) : (
          <span style={{ display: 'inline-flex', color: theme.textDim }}>
            <Icon.chevron />
          </span>
        )}
        {active && allowClear && (
          <span onClick={(e) => { e.stopPropagation(); onChange(null) }}
            style={{
              fontSize: 14, lineHeight: 0, color: theme.textDim, cursor: 'pointer',
              padding: '0 0 0 2px',
            }}>×</span>
        )}
      </button>
      {open && (
        <Dropdown theme={theme} options={opts} value={value}
          onPick={(v) => { onChange(v); setOpen(false) }}
        />
      )}
    </div>
  )
}

// Dropdowns with more than this many options get a search box — short lists
// (e.g. a 2-3 value "Typ salonu") don't need one and it'd just be clutter.
const SEARCHABLE_THRESHOLD = 6

function Dropdown({ theme, options, value, onPick }) {
  const [query, setQuery] = useState('')
  const searchable = options.length > SEARCHABLE_THRESHOLD
  const q = query.trim().toLowerCase()
  const filtered = searchable && q
    ? options.filter(o => o.label.toLowerCase().includes(q))
    : options
  return (
    <div style={{
      position: 'absolute', top: '100%', left: 0, marginTop: 4,
      minWidth: 200, maxWidth: 360, maxHeight: 320, overflowY: 'auto',
      zIndex: 51,
      background: theme.surface, border: `1px solid ${theme.borderStrong}`,
      borderRadius: 8, padding: 4,
      boxShadow: '0 10px 28px rgba(0,0,0,0.14)',
    }}>
      {searchable && (
        <div style={{ padding: '2px 2px 6px', position: 'sticky', top: 0, background: theme.surface }}>
          <input
            autoFocus
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            placeholder="Szukaj…"
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '6px 8px', fontSize: 12.5, fontFamily: T.fontUI,
              border: `1px solid ${theme.border}`, borderRadius: 6,
              outline: 'none', color: theme.text, background: theme.bg,
            }}
          />
        </div>
      )}
      {filtered.length === 0 && (
        <div style={{ padding: '7px 10px', fontSize: 12, color: theme.textDim }}>
          {searchable && q ? 'Brak wyników' : 'Brak opcji'}
        </div>
      )}
      {filtered.map(opt => {
        const isSelected = opt.value === value
        return (
          <div key={String(opt.value)} onClick={() => onPick(opt.value)} style={{
            padding: '7px 10px', borderRadius: 5,
            fontSize: 12.5, cursor: 'pointer',
            background: isSelected ? theme.bgSub : 'transparent',
            color: theme.text,
            fontFamily: T.fontUI,
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = theme.bgSub}
          onMouseLeave={(e) => e.currentTarget.style.background = isSelected ? theme.bgSub : 'transparent'}
          >
            {opt.label}
          </div>
        )
      })}
    </div>
  )
}

// ─ Segmented (inline radio-pill) ────────────────────────────────
function SegmentedField({ field, theme }) {
  const { label, value, options, onChange } = field
  const opts = normalizeOptions(options)
  return (
    <PillFrame theme={theme} active={false} padding="3px 4px" style={{ gap: 2 }}>
      {label && (
        <span style={{
          fontSize: 10, fontWeight: 700, color: theme.textDim,
          letterSpacing: 0.5, textTransform: 'uppercase',
          padding: '0 8px', whiteSpace: 'nowrap',
        }}>{label}</span>
      )}
      {opts.map(o => {
        const isOn = o.value === value
        return (
          <button key={String(o.value)} onClick={() => onChange(o.value)} style={{
            padding: '4px 12px', borderRadius: 5,
            border: 'none',
            background: isOn ? theme.surface : 'transparent',
            color: isOn ? theme.text : theme.textSub,
            fontSize: 12.5, fontWeight: isOn ? 600 : 500,
            cursor: 'pointer',
            boxShadow: isOn ? `0 1px 2px ${theme.border}` : 'none',
            fontFamily: T.fontUI,
            transition: 'background 100ms',
          }}>{o.label}</button>
        )
      })}
    </PillFrame>
  )
}

// ─ Date input ───────────────────────────────────────────────────
function DateField({ field, theme }) {
  const { label, value, onChange, min, max } = field
  const active = !!value
  return (
    <PillFrame theme={theme} active={active} padding="4px 10px 4px 12px">
      {label && (
        <span style={{
          fontSize: 10, fontWeight: 700, color: theme.textDim,
          letterSpacing: 0.5, textTransform: 'uppercase', whiteSpace: 'nowrap',
        }}>{label}</span>
      )}
      <input
        type="date"
        value={value || ''}
        min={min} max={max}
        onChange={(e) => onChange(e.target.value || null)}
        style={{
          border: 'none', background: 'transparent',
          fontFamily: T.fontMono, fontSize: 12.5, fontWeight: 500,
          color: theme.text, padding: '2px 0', outline: 'none',
          cursor: 'pointer', minWidth: 110,
        }}
      />
    </PillFrame>
  )
}

// ─ Date range (two pills joined with em-dash) ───────────────────
function DateRangeField({ field, theme }) {
  const { labelFrom = 'Od', labelTo = 'Do',
          valueFrom, valueTo, onChangeFrom, onChangeTo } = field
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <DateField field={{ label: labelFrom, value: valueFrom, onChange: onChangeFrom }} theme={theme} />
      <span style={{ color: theme.textDim, fontFamily: T.fontMono, fontSize: 12 }}>—</span>
      <DateField field={{ label: labelTo, value: valueTo, onChange: onChangeTo }} theme={theme} />
    </span>
  )
}

// ─ Toggle (checkbox-pill) ───────────────────────────────────────
function ToggleField({ field, theme }) {
  const { label, value, onChange } = field
  const on = !!value
  return (
    <button onClick={() => onChange(!on)} style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '6px 10px',
      background: on ? `${theme.accent}12` : theme.surface,
      border: `1px solid ${on ? theme.accent + '55' : theme.border}`,
      borderRadius: 7, cursor: 'pointer', fontFamily: T.fontUI,
      fontSize: 12.5, fontWeight: on ? 600 : 500,
      color: on ? theme.accent : theme.textSub,
    }}>
      <span style={{
        width: 14, height: 14, borderRadius: 4,
        border: `1.5px solid ${on ? theme.accent : theme.borderStrong}`,
        background: on ? theme.accent : 'transparent',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontSize: 10, lineHeight: 1, transition: 'all 100ms',
      }}>{on ? '✓' : ''}</span>
      {label}
    </button>
  )
}

// ─ Number input ─────────────────────────────────────────────────
function NumberField({ field, theme }) {
  const { label, value, onChange, min, max, step = 1, width = 80 } = field
  const active = isActiveValue(value)
  return (
    <PillFrame theme={theme} active={active} padding="4px 10px 4px 12px">
      {label && (
        <span style={{
          fontSize: 10, fontWeight: 700, color: theme.textDim,
          letterSpacing: 0.5, textTransform: 'uppercase', whiteSpace: 'nowrap',
        }}>{label}</span>
      )}
      <input
        type="number"
        value={value ?? ''}
        min={min} max={max} step={step}
        onChange={(e) => {
          const v = e.target.value
          onChange(v === '' ? null : Number(v))
        }}
        style={{
          border: 'none', background: 'transparent',
          fontFamily: T.fontMono, fontSize: 12.5, fontWeight: 500,
          color: theme.text, padding: '2px 0', outline: 'none',
          width,
        }}
      />
    </PillFrame>
  )
}
